import { useMemo, useState } from "react";
import { MessageSquare, UserPlus, User as UserIcon } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { formatPhoneForDisplay } from "@/lib/format-phone";
import { repairSharedContacts } from "@/lib/repair-shared-contact";
import { SharedContactSaveDialog } from "./SharedContactSaveDialog";

export interface SharedContactInfo {
  name: string;
  phones: string[];
  emails: string[];
  org: string | null;
}

interface SharedContactBubbleProps {
  metadata: any;
  workspaceId: string;
  integrationId?: string | null;
  isUserMessage: boolean;
  /** Body da mensagem — recupera "Nome: 5511…" em registros antigos. */
  fallbackText?: string | null;
}

export function SharedContactBubble({
  metadata,
  workspaceId,
  integrationId,
  isUserMessage,
  fallbackText,
}: SharedContactBubbleProps) {
  const contacts = useMemo(
    () => repairSharedContacts(metadata, fallbackText),
    [metadata, fallbackText],
  );
  const [dialogState, setDialogState] = useState<
    | { open: false }
    | { open: true; contact: SharedContactInfo; mode: "save" | "chat" }
  >({ open: false });

  if (contacts.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 mb-1">
      {contacts.map((c, idx) => {
        const initials = c.name
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((s) => s[0]?.toUpperCase() ?? "")
          .join("") || "?";
        const hasPhone = c.phones.length > 0;
        return (
          <div
            key={idx}
            className={`rounded-lg overflow-hidden border ${
              isUserMessage ? "border-white/20 bg-white/10" : "border-border bg-muted/50"
            } min-w-[240px] max-w-[320px]`}
          >
            <div className="flex items-center gap-3 p-3">
              <Avatar className="w-10 h-10">
                <AvatarFallback className="bg-primary/10 text-primary">
                  {initials || <UserIcon className="w-5 h-5" />}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{c.name}</p>
                {c.phones.length > 0 && (
                  <p className="text-xs text-muted-foreground truncate">
                    {formatPhoneForDisplay(c.phones[0]) || c.phones[0]}
                    {c.phones.length > 1 && ` +${c.phones.length - 1}`}
                  </p>
                )}
                {c.org && (
                  <p className="text-xs text-muted-foreground truncate">{c.org}</p>
                )}
              </div>
            </div>
            <div className="flex border-t border-inherit divide-x divide-inherit">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="flex-1 rounded-none h-9 text-xs font-medium"
                disabled={!hasPhone}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setDialogState({ open: true, contact: c, mode: "chat" });
                }}
              >
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                Conversar
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="flex-1 rounded-none h-9 text-xs font-medium"
                disabled={!hasPhone}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setDialogState({ open: true, contact: c, mode: "save" });
                }}
              >
                <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                Adicionar contato
              </Button>
            </div>
          </div>
        );
      })}

      {dialogState.open && (
        <SharedContactSaveDialog
          open={dialogState.open}
          onOpenChange={(open) => !open && setDialogState({ open: false })}
          workspaceId={workspaceId}
          integrationId={integrationId ?? null}
          contact={dialogState.contact}
          mode={dialogState.mode}
        />
      )}
    </div>
  );
}
